// A product's web app signs people in through the Foundation (F1-lite: a first-party client of a product
// organisation). Its private key is generated on the product's own server and never leaves it; the console
// receives only public keys in a manifest and refuses anything that carries a private part.

import { capabilitiesFrom } from "./clients";

export interface WebAppClient {
  name: string;
  grant_types: ["authorization_code"];
  redirect_uris: [string];
  jwks: { keys: Record<string, unknown>[] };
  scopes: string[];
  first_party: boolean;
}

export const WEB_APP_DEFAULT_SCOPES = "identity.self:read";

const PRIVATE_MEMBERS = ["d", "p", "q", "dp", "dq", "qi", "oth", "k"];

/** Shared by manifest registration and the legacy body helper: private material never leaves the browser. */
export function publicKeySet(value: unknown): { keys: Record<string, unknown>[] } {
  const keys = typeof value === "object" && value !== null ? (value as { keys?: unknown }).keys : undefined;
  if (!Array.isArray(keys) || keys.length === 0) throw new Error("Give a public key set with a keys array.");
  if (keys.some((key) => typeof key !== "object" || key === null || PRIVATE_MEMBERS.some((member) => member in key) || (key as { kty?: string }).kty === "oct")) {
    throw new Error("That key set contains a private key. Use only the public key set.");
  }
  return { keys: keys as Record<string, unknown>[] };
}

/** Public key URLs and sign-in return addresses share the same boundary. The Foundation owns DNS/egress validation. */
export function publicHttpsAddress(address: string): void {
  const url = new URL(address);
  if (
    url.protocol !== "https:" ||
    url.username ||
    url.password ||
    url.hash ||
    /^(localhost|127\.|\[?::1\]?)/i.test(url.hostname)
  )
    throw new Error(
      "Use public HTTPS addresses without passwords or fragments.",
    );
}

export function webAppClient(
  name: string,
  redirectUri: string,
  jwksText: string,
  firstParty: boolean,
  scopesText: string,
): WebAppClient {
  let jwks: unknown;
  try {
    jwks = JSON.parse(jwksText);
  } catch {
    throw new Error("Paste the public key set (JSON with a keys array).");
  }
  const publicKeys = publicKeySet(jwks);
  if (!/^https:\/\/[^/\s#]+\/\S*$/.test(redirectUri)) throw new Error("The redirect URI must be an absolute https URL.");
  return {
    name,
    grant_types: ["authorization_code"],
    redirect_uris: [redirectUri],
    jwks: publicKeys,
    scopes: capabilitiesFrom(scopesText),
    first_party: firstParty,
  };
}
